import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import ArrowLeftIcon from '@/assets/icons/arrow-left-icon.svg?react';
import ArrowRightIcon from '@/assets/icons/arrow-right-icon.svg?react';
import DismissIcon from '@/assets/icons/dismiss-icon.svg?react';
import DownloadIcon from '@/assets/icons/download-icon.svg?react';
import PlayIcon from '@/assets/icons/play-icon.svg?react';
import { useMediaMessageCarousel } from '@/hooks/useMediaMessageCarousel';
import type { UploadedFile } from '@/types/message.types';

interface CarouselHeaderProps {
  currentIndex: number;
  totalFiles: number;
  onDownload: () => void;
  onClose: () => void;
}

interface NavigationButtonProps {
  direction: 'prev' | 'next';
  onClick: () => void;
}

interface MediaItemProps {
  file: UploadedFile;
}

interface ThumbnailStripProps {
  files: UploadedFile[];
  currentIndex: number;
  onThumbnailClick: (index: number) => void;
}

const CarouselHeader: React.FC<CarouselHeaderProps> = ({
  currentIndex,
  totalFiles,
  onDownload,
  onClose,
}) => {
  const { t } = useTranslation('common');

  return (
    <>
      {/* Media counter */}
      <div className="sf:absolute sf:top-4 sf:left-4 sf:z-10 sf:bg-white/20 sf:backdrop-blur-sm sf:rounded-full sf:px-3 sf:py-1 sf:text-white sf:text-sm">
        {currentIndex + 1} / {totalFiles}
      </div>

      {/* Action buttons */}
      <div className="sf:absolute sf:top-4 sf:right-4 sf:z-10 sf:flex sf:gap-2">
        <button
          onClick={onDownload}
          className="sf:w-10 sf:h-10 sf:bg-white/20 sf:backdrop-blur-sm sf:rounded-full sf:flex sf:items-center sf:justify-center sf:text-white sf:hover:bg-white/30 sf:transition-colors sf:cursor-pointer"
          aria-label={t('mediaMessageCarousel.download')}
        >
          <DownloadIcon className="sf:w-5 sf:h-5 sf:text-white" />
        </button>

        <button
          onClick={onClose}
          className="sf:w-10 sf:h-10 sf:bg-white/20 sf:backdrop-blur-sm sf:rounded-full sf:flex sf:items-center sf:justify-center sf:text-white sf:hover:bg-white/30 sf:transition-colors sf:cursor-pointer"
          aria-label={t('mediaMessageCarousel.closeCarousel')}
        >
          <DismissIcon className="sf:w-6 sf:h-6 sf:text-white" />
        </button>
      </div>
    </>
  );
};

const NavigationButton: React.FC<NavigationButtonProps> = ({
  direction,
  onClick,
}) => {
  const { t } = useTranslation('common');
  const isNext = direction === 'next';
  const positionClass = isNext ? 'sf:right-4' : 'sf:left-4';
  const IconComponent = isNext ? ArrowRightIcon : ArrowLeftIcon;
  const label = isNext
    ? t('mediaMessageCarousel.nextMedia')
    : t('mediaMessageCarousel.previousMedia');

  return (
    <button
      onClick={onClick}
      className={`sf:absolute ${positionClass} sf:top-1/2 sf:-translate-y-1/2 sf:w-12 sf:h-12 sf:bg-white/20 sf:backdrop-blur-sm sf:rounded-full sf:flex sf:items-center sf:justify-center sf:text-white sf:hover:bg-white/30 sf:transition-colors sf:cursor-pointer`}
      aria-label={label}
    >
      <IconComponent className="sf:w-6 sf:h-6 sf:text-white" />
    </button>
  );
};

const MediaItem: React.FC<MediaItemProps> = ({ file }) => {
  const { t } = useTranslation('common');
  const isImage = file.mimeType?.startsWith('image/');
  const isVideo = file.mimeType?.startsWith('video/');

  const mediaContainerClass = `sf:flex sf:items-center sf:justify-center sf:h-full sf:w-full sf:opacity-100 sf:transition-opacity sf:duration-300`;
  const mediaClass = `sf:max-w-[80%] sf:max-h-[70%] sf:object-contain sf:rounded-lg sf:transition-all sf:duration-300 sf:shadow-2xl`;

  if (isImage) {
    return (
      <div className={mediaContainerClass}>
        <img
          src={file.url}
          alt={file.filename || 'Media'}
          className={mediaClass}
          draggable={false}
        />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className={mediaContainerClass}>
        <video src={file.url} controls className={mediaClass} autoPlay>
          {t('mediaMessageCarousel.unsupportedMediaType')}
        </video>
      </div>
    );
  }

  return (
    <div className="sf:flex sf:items-center sf:justify-center sf:h-full sf:w-full sf:text-white">
      <span>{t('mediaMessageCarousel.unsupportedMediaType')}</span>
    </div>
  );
};

const ThumbnailStrip: React.FC<ThumbnailStripProps> = ({
  files,
  currentIndex,
  onThumbnailClick,
}) => (
  <div className="sf:absolute sf:bottom-4 sf:left-1/2 sf:-translate-x-1/2 sf:flex sf:space-x-2 sf:bg-white/20 sf:backdrop-blur-sm sf:rounded-full sf:px-4 sf:py-2 sf:max-w-xs sf:overflow-x-auto">
    {files.map((file, index) => {
      const isActive = index === currentIndex;

      return (
        <button
          key={file.fileId || index}
          onClick={() => onThumbnailClick(index)}
          className={classNames({
            'sf:w-8 sf:h-8 sf:rounded-full sf:flex-shrink-0 sf:overflow-hidden sf:border-2 sf:transition-colors sf:cursor-pointer':
              true,
            'sf:border-white': isActive,
            'sf:border-white/50 sf:hover:border-white/80': !isActive,
          })}
        >
          {file.mimeType?.startsWith('image/') ? (
            <img
              src={file.url}
              alt=""
              className="sf:w-full sf:h-full sf:object-cover"
            />
          ) : (
            <div className="sf:w-full sf:h-full sf:bg-gray-800 sf:flex sf:items-center sf:justify-center">
              <PlayIcon className="sf:w-3 sf:h-3 sf:text-white" />
            </div>
          )}
        </button>
      );
    })}
  </div>
);

/**
 * MediaMessageCarousel - Full-screen media message carousel with overlay mask
 */
export const MediaMessageCarousel: React.FC = () => {
  const { isOpen, files, currentIndex, actions } = useMediaMessageCarousel();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: currentIndex,
    align: 'center',
    containScroll: 'trimSnaps',
    dragFree: false,
    duration: 20,
  });

  // Sync Embla carousel with store state
  useEffect(() => {
    if (!isOpen) return;

    if (emblaApi) {
      emblaApi.scrollTo(currentIndex, false);
    }
  }, [emblaApi, currentIndex, isOpen]);

  // Listen to Embla carousel changes
  useEffect(() => {
    if (!isOpen) return;

    if (emblaApi) {
      const onSelect = () => {
        actions.setCurrentIndex(emblaApi.selectedScrollSnap());
      };

      emblaApi.on('select', onSelect);

      return () => {
        emblaApi.off('select', onSelect);
      };
    }
  }, [emblaApi, actions, isOpen]);

  // Keyboard navigation and body scroll prevention
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const keyActions: Record<string, () => void> = {
        Escape: actions.closeCarousel,
        ArrowLeft: actions.prevSlide,
        ArrowRight: actions.nextSlide,
      };
      const action = keyActions[event.key];

      action?.();
    };

    // Prevent body scroll and add keyboard listener
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, actions]);

  const handleDownload = useCallback(async (file: UploadedFile) => {
    const link = document.createElement('a');

    // Extract filename from the path
    const filename = file.filename?.split('/').pop() || `media-${Date.now()}`;
    const response = await fetch(file.url);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  // Early return if not open or no files
  if (!isOpen || files.length === 0) {
    return null;
  }

  const currentFile = files[currentIndex];
  const hasMultipleFiles = files.length > 1;

  return (
    <div className="sf:fixed sf:inset-0 sf:bg-black/90 sf:z-[9999] sf:flex sf:items-center sf:justify-center">
      <CarouselHeader
        currentIndex={currentIndex}
        totalFiles={files.length}
        onDownload={() => handleDownload(currentFile)}
        onClose={actions.closeCarousel}
      />

      {/* Carousel container */}
      <div className="sf:w-full sf:h-full sf:max-w-6xl sf:max-h-full sf:mx-auto sf:px-4 sf:py-8">
        <div className="embla sf:h-full" ref={emblaRef}>
          <div className="embla__container sf:flex sf:h-full sf:transition-transform sf:duration-300 sf:ease-out">
            {files.map((file, index) => (
              <div
                key={file.fileId || index}
                className="embla__slide sf:flex sf:items-center sf:justify-center sf:flex-[0_0_100%] sf:min-w-0 sf:h-full sf:transition-transform sf:duration-300 sf:ease-in-out"
              >
                <MediaItem file={file} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation buttons */}
      {hasMultipleFiles && (
        <>
          <NavigationButton direction="prev" onClick={actions.prevSlide} />
          <NavigationButton direction="next" onClick={actions.nextSlide} />
        </>
      )}

      {/* Thumbnails strip */}
      {hasMultipleFiles && (
        <ThumbnailStrip
          files={files}
          currentIndex={currentIndex}
          onThumbnailClick={actions.setCurrentIndex}
        />
      )}
    </div>
  );
};
